import { useEffect, useRef, useState } from 'react';
import { Button, Divider, Flex, Progress, SegmentedControl, Stack, Table, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import confetti from 'canvas-confetti';
import { godModeStore } from './godModeStore';
import { getDivineWisdomOfTheDay } from './divineWisdom';
import { startBreakout, isArcadeUnlocked, unlockArcade } from './breakout';
import { achievementsStore } from '../achievements/achievementsStore';
import { ACHIEVEMENTS } from '../achievements/achievements';
import { AchievementsModal } from '../achievements/AchievementsModal';
import { startShire, stopShire, getShireVariant, isShireDiscovered } from '../shire';
import classes from './GodModeDashboard.module.css';

// The fake God-Mode "control panel". Every button here is cosmetic — it changes nothing real.
export function GodModeDashboard() {
    const session = godModeStore.getSession();
    const [working, setWorking] = useState(false);
    const [progress, setProgress] = useState(0);
    const [shake, setShake] = useState(false);
    const [arcadeClicks, setArcadeClicks] = useState(0);
    const arcadeUnlocked = isArcadeUnlocked();
    const shireFound = isShireDiscovered();
    const [sanctuary, setSanctuary] = useState<string>(getShireVariant() ?? 'off');
    const [, forceRender] = useState(0);
    const tickRef = useRef<number | null>(null);

    // Re-render when God Mode expires while this modal is open, so the controls give way to the
    // "privileges expired" notice instead of staying live until the modal is reopened.
    useEffect(() => godModeStore.subscribe(() => forceRender((n) => n + 1)), []);

    // Stop the Bend Reality interval if the modal is closed mid-animation (no setState on an unmounted
    // component, no stray completion notification).
    useEffect(() => () => { if (tickRef.current !== null) window.clearInterval(tickRef.current); }, []);

    // Keep (or drop) the woodland theme — it persists across reloads. Silent: no dramatic entrance here.
    const changeSanctuary = (v: string) => {
        setSanctuary(v);
        if (v === 'off') stopShire();
        else startShire(v as 'shire' | 'rivendell', { silent: true });
    };

    if (!session) {
        return (
            <Text ta="center" py="md">
                ⚡ Your divine privileges have expired. Regular Backoffice competence is now required.
            </Text>
        );
    }

    const unlockedCount = achievementsStore.getUnlocked().length;
    const wisdom = getDivineWisdomOfTheDay();

    // zIndex above the Mantine modal (default 200) so confetti isn't hidden behind the dashboard.
    const summonConfetti = () =>
        confetti({ particleCount: 140, spread: 90, origin: { y: 0.6 }, zIndex: 10000, colors: ['#FFD700', '#FFC107', '#FF9800'] });

    const generateCoffee = () => {
        confetti({ particleCount: 60, spread: 60, origin: { y: 0.7 }, scalar: 1.1, zIndex: 10000 });
        notifications.show({ title: '☕ Coffee generated', message: 'Freshly brewed by divine intervention.', color: 'yellow' });
    };

    const bendReality = () => {
        if (working) return;
        setWorking(true);
        setProgress(0);
        const started = Date.now();
        const durationMs = 1600;
        tickRef.current = window.setInterval(() => {
            const p = Math.min(100, ((Date.now() - started) / durationMs) * 100);
            setProgress(p);
            if (p >= 100) {
                if (tickRef.current !== null) window.clearInterval(tickRef.current);
                tickRef.current = null;
                setWorking(false);
                notifications.show({ title: '🌌 Reality', message: 'Reality sufficiently bent to your will.', color: 'grape' });
                window.setTimeout(() => setProgress(0), 700);
            }
        }, 60);
    };

    const fixEverything = () => {
        notifications.show({ title: '✨ Fix Everything', message: 'Everything has been fixed.', color: 'green' });
        window.setTimeout(
            () => notifications.show({ title: 'Fix Everything', message: 'No actual changes were made.', color: 'gray' }),
            2000,
        );
    };

    const skipMeeting = () => {
        setShake(true);
        window.setTimeout(() => setShake(false), 600);
        notifications.show({ title: '🚫 Skip the Meeting', message: 'Even God Mode has limits.', color: 'red' });
    };

    const openAchievements = () =>
        modals.open({ title: '🏆 My Achievements', centered: true, size: 'md', children: <AchievementsModal /> });

    const launchArcade = () => {
        modals.closeAll();
        startBreakout();
    };

    // The third discovery layer — five clicks to unlock the hidden game (single click once unlocked).
    const clickClassified = () => {
        if (arcadeUnlocked) {
            launchArcade();
            return;
        }
        const n = arcadeClicks + 1;
        setArcadeClicks(n);
        if (n >= 5) {
            unlockArcade();
            notifications.show({ title: '🔓 ARCADE MODE UNLOCKED', message: 'Productivity has left the building.', color: 'grape' });
            launchArcade();
        }
    };

    return (
        <Flex gap="lg" align="flex-start" direction={{ base: 'column', sm: 'row' }}>
            {/* Left: divine readouts */}
            <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
                <div>
                    <Text fw={700} className={classes.section}>✨ DIVINE WISDOM OF THE DAY</Text>
                    <Text fs="italic" mt={4}>“{wisdom.text}”</Text>
                    {wisdom.author ? <Text size="sm" c="dimmed" mt={2}>— {wisdom.author}</Text> : null}
                </div>

                <Divider />

                <div>
                    <Text fw={700} className={classes.section}>YOUR DIVINE POWERS</Text>
                    <Table withRowBorders={false} verticalSpacing={4}>
                        <Table.Tbody>
                            {session.powers.map((p) => (
                                <Table.Tr key={p.label}>
                                    <Table.Td>{p.label}</Table.Td>
                                    <Table.Td style={{ textAlign: 'right' }}>
                                        <Text span fw={600} className={classes.status}>{p.value}</Text>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </div>

                <Text size="xs" c="dimmed">
                    Achievements unlocked: {unlockedCount} / {ACHIEVEMENTS.length}
                </Text>

                <Text
                    className={classes.classified}
                    onClick={clickClassified}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            clickClassified();
                        }
                    }}
                    role="button"
                    tabIndex={0}
                >
                    {arcadeUnlocked
                        ? '🎮 ARCADE MODE — Launch'
                        : '🎮 CLASSIFIED POWER — Access denied to mortals.'}
                </Text>
            </Stack>

            {/* Right: divine actions (cosmetic buttons) */}
            <Stack gap="xs" w={{ base: '100%', sm: 220 }}>
                <Text fw={700} className={classes.section}>DIVINE ACTIONS</Text>
                <Button fullWidth variant="subtle" onClick={openAchievements}>View Achievements 🏆</Button>
                <Button fullWidth variant="light" color="yellow" onClick={summonConfetti}>Summon Confetti</Button>
                <Button fullWidth variant="light" color="yellow" onClick={generateCoffee}>Generate Coffee ☕</Button>
                <Button fullWidth variant="light" color="grape" loading={working} onClick={bendReality}>Bend Reality</Button>
                <Button fullWidth variant="light" color="green" onClick={fixEverything}>Fix Everything</Button>
                <Button fullWidth variant="light" color="red" className={shake ? classes.shake : undefined} onClick={skipMeeting}>Skip the Meeting 🔒</Button>
                {working || progress > 0 ? (
                    <Progress value={progress} color="grape" transitionDuration={100} />
                ) : null}

                {shireFound ? (
                    <>
                        <Text size="xs" c="dimmed" mt={4}>🌿 Woodland Sanctuary</Text>
                        <SegmentedControl
                            fullWidth
                            size="xs"
                            value={sanctuary}
                            onChange={changeSanctuary}
                            data={[
                                { label: 'Off', value: 'off' },
                                { label: 'Shire', value: 'shire' },
                                { label: 'Rivendell', value: 'rivendell' },
                            ]}
                        />
                    </>
                ) : null}
            </Stack>
        </Flex>
    );
}
